import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getGalleryInner } from "../Service/Api";
import Header from "../Component/Header";
import LazyLoad from "react-lazyload";

const Gallery = () => {
  useEffect(() => {
    document.title = "Gallery - The Air Force School Subroto Park";
  }, []);

  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [filterData, setFilteredData] = useState([]);
  const [selectedYear, setSelectedYear] = useState();
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [monthOptions, setMonthOptions] = useState(["All"]);
  const [yearRanges, setYearRanges] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [archivedData, setArchivedData] = useState([]);
  const [loading, setLoading] = useState(true); // Loading state

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Get current year for default selection
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    setSelectedYear(`${currentYear}`);
  }, []);

  // Fetch data and set the gallery data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const galleryData1 = await getGalleryInner();
        const galleryData = galleryData1.filter(
          (item) => item.category === "PhotoGallery",
        );
        setData(galleryData);

        // Get unique years and months for filtering
        const yearsSet = new Set();
        const monthsSet = new Set();

        galleryData.forEach((item) => {
          if (item.date) {
            const date = new Date(item.date);
            yearsSet.add(date.getFullYear());
            monthsSet.add(date.getMonth());
          }
        });

        const uniqueYearRanges = Array.from(yearsSet).sort((a, b) => b - a);
        setYearRanges(uniqueYearRanges);

        const monthOptions = Array.from(monthsSet)
          .sort((a, b) => a - b)
          .map((month) => monthNames[month]);
        setMonthOptions(["All", ...monthOptions]);

        setLoading(false); // Data has been fetched, update loading state
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter data based on selected year, month, and search query
  useEffect(() => {
    let filteredData = data;

    // Separate archived and valid data
    const archived = filteredData.filter((item) => !item.date); // Archived items (no date)
    const validData = filteredData.filter((item) => item.date); // Valid items (with date)
    if (
      selectedYear === "All" &&
      selectedMonth === "All" &&
      searchQuery === ""
    ) {
      setFilteredData(validData);
      setArchivedData(archived); // Show archived data when no filters are applied
      return;
    }
    // If "Archive" is selected, show only archived data
    if (selectedYear === "Archive") {
      setFilteredData([]);
      setArchivedData(archived);
      return;
    }

    // If a specific year is selected, filter by year and month
    if (selectedYear !== "All") {
      const year = parseInt(selectedYear);

      filteredData = validData.filter((item) => {
        const itemDate = item.date?.trim();
        if (itemDate) {
          const date = new Date(itemDate);
          const itemYear = date.getFullYear();
          const itemMonth = date.getMonth();
          return (
            itemYear === year &&
            (selectedMonth === "All" || monthNames[itemMonth] === selectedMonth)
          );
        }
        return false;
      });
    }

    // If a month is selected but no year, filter by month
    if (selectedMonth !== "All" && selectedYear === "All") {
      filteredData = validData.filter((item) => {
        const itemDate = item.date?.trim();
        if (itemDate) {
          const itemMonth = new Date(itemDate).getMonth();
          return monthNames[itemMonth] === selectedMonth;
        }
        return false;
      });
    }

    // Search filtering
    if (searchQuery) {
      filteredData = filteredData.filter((item) =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    // Sort the filtered data by date in descending order
    filteredData.sort((a, b) => new Date(b.date) - new Date(a.date));

    // Set filtered data
    setFilteredData(filteredData);
    setArchivedData(archived); // Set archived data separately
  }, [selectedYear, selectedMonth, searchQuery, data]);

  const handleModal = (id) => {
    navigate(`/SubGallery?id=${id}`);
  };

  return (
    <>
      <Header />
      <div className="innerslide">
        <ul className="breadcrumb">
          <li>
            <Link to="/"> Home</Link>
          </li>
          <li>Gallery</li>
          <li>Gallery</li>
        </ul>
      </div>
      <div className="innersec">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <h1>Gallery</h1>
            </div>
          </div>
          <div className="row tabs-dynamic">
            <div className="col-xxl-3 col-xl-3 col-lg-6 col-12">
              <div className="count-val">
                <p>Total Categories: {filterData.length}</p>
              </div>
            </div>
            <div className="col-xxl-3 col-xl-3 col-lg-6 col-12">
              <div className="month-selection">
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="yearSelection"
                >
                  <option value="All">All</option>
                  {yearRanges.map((yr) => (
                    <option key={yr} value={yr}>
                      {yr}
                    </option>
                  ))}
                  <option value="Archive">Archive</option>
                </select>
              </div>
            </div>
            <div className="col-xxl-3 col-xl-3 col-lg-6 col-12">
              <div className="month-selection">
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="monthSelect"
                >
                  {monthOptions.map((month, index) => (
                    <option key={index} value={month}>
                      {month}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-xxl-3 col-xl-3 col-lg-6 col-12">
              <div className="searchBox">
                <input
                  type="text"
                  id="myInput"
                  name="name"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search Here.."
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div>
              <img
                src="https://entab-s3-bucket1.s3.ap-south-1.amazonaws.com/TAFS/public/Images/loading.gif"
                className="img-fluid loadingimg"
              />
            </div>
          ) : (
            selectedYear === "Archive" &&
            archivedData.length > 0 && (
              <div className="row years year2024 showYear">
                {archivedData.map((item, index) => {
                  return (
                    <div
                      className="col-xl-3 col-lg-4 col-md-6 col-sm-12 galleryCount"
                      key={index}
                    >
                      <div className="gallery-inner">
                        <div className="gallery-blks">
                          <div className="backDiv"></div>
                          <LazyLoad>
                            <img
                              src={`https://entab-s3-bucket1.s3.ap-south-1.amazonaws.com/${item?.attachments[0]}`}
                              className="img-fluid"
                              alt="No Date Available"
                            />
                          </LazyLoad>
                        </div>
                        <div className="gallery-content">
                          <div className="gallery-date">
                            <div className="gall-num">
                              <h5>
                                <span>Number Of Photos :</span>
                                {item?.attachments.length}
                              </h5>
                            </div>
                          </div>
                          <div className="gallery-title">
                            <h3>{item.title}</h3>
                          </div>
                          <Link
                            to={`/SubGallery?id=${item._id}`}
                            className="view-more"
                          >
                            View More <i className="bi bi-arrow-right"></i>
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}

          {/* Show Filtered Data */}
          {selectedYear !== "Archive" && filterData.length > 0 && (
            <div className="row years year2024 showYear">
              {filterData.map((item, index) => {
                const date = new Date(item.date);
                const day = date.getDate();
                const month = monthNames[date.getMonth()];
                return (
                  <div
                    className="col-xl-3 col-lg-4 col-md-6 col-sm-12 galleryCount"
                    key={index}
                  >
                    <div className="gallery-inner">
                      <div className="gallery-blks">
                        <div className="backDiv"></div>
                        <LazyLoad>
                          {" "}
                          <img
                            src={`https://entab-s3-bucket1.s3.ap-south-1.amazonaws.com/${item?.attachments[0]}`}
                            className="img-fluid"
                            alt="The Air Force School Subroto Park"
                          />{" "}
                        </LazyLoad>
                      </div>
                      <div className="gallery-content">
                        <div className="gallery-date">
                          <h6>
                            {day} <span>{month}</span>
                          </h6>
                          <div className="gall-num">
                            <h5>
                              <span>Number Of Photos :</span>
                              {item?.attachments.length}
                            </h5>
                          </div>
                        </div>
                        <div className="gallery-title">
                          <h3>{item.title}</h3>
                        </div>
                        <a
                          onClick={() => navigate(`/SubGallery?id=${item._id}`)}
                          className="view-more"
                        >
                          View More <i className="bi bi-arrow-right"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Gallery;
